import { isStandardRole, useUserRoleForCurrentWorkspace } from '@/features/roles/hook';
import { authStore } from '@/services/auth/store';
import { getMasqBarVisibility, setMasqBarVisibility } from '@/utils/masq';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { Role } from '@customerio/hydra-permissions/roles';
import {
  Box,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuFooter,
  DropdownMenuGroup,
  DropdownMenuItem,
  Icon,
  Image,
  Paragraph,
  PlainButton,
  Text,
  Toggle,
} from '@customerio/pluma-components/react';
import { useStore } from '@nanostores/react';
import { useCallback, useRef, useState } from 'react';
import { IconButton } from '../icon-button';
import { useCurrentWorkspace } from '@/context/current-workspace';
import { getWorkspaceSettingsLink } from '@/hooks/get-workspace-settings-link';
import { useSampleWorkspace } from '@/features/workspaces/sample-workspace';
import useSetupGuideExperiment from '@/features/experiments/setup-guide/hook';
import { useThemeContext } from '@/context/theme';
import { useFeatureFlag, FeatureFlag } from '@/services/auth/feature-flags';
import heartPride from '@/assets/images/heart-pride.svg';
import { generatePath } from '@/services/router/generate-path';
import * as styles from './index.css';
import { BRAND_EVERGREEN, BRAND_VERDANT, fireConfetti } from '@/components/feedback/confetti';
import confetti from 'canvas-confetti';

function currentUserRoleString(role: string) {
  const roleStrings: Record<Role, string> = {
    [Role.Admin]: 'an Account admin',
    [Role.Manager]: 'a Workspace admin',
    [Role.Author]: 'an Author',
    [Role.AuthorRedacted]: 'an Author',
    [Role.AuthorRedactedSensitive]: 'an Author',
    [Role.AuthorRedactedAll]: 'an Author',
    [Role.Viewer]: 'a Viewer',
    [Role.ViewerRedacted]: 'a Viewer',
    [Role.ViewerRedactedSensitive]: 'a Viewer',
    [Role.ViewerRedactedAll]: 'a Viewer',
  };

  if (isStandardRole(role)) {
    return `You're ${roleStrings[role]}`;
  }

  return role;
}

const heartShape = confetti.shapeFromPath({
  path: 'M14.7,2.3c-1.6-1.6-4.3-1.6-6,0l-.8.8-.8-.8C5.6.6,2.9.6,1.3,2.3s-1.6,4.3,0,6l.8.8h0s6,6,6,6h0s0,0,0,0l6-6h0s.8-.8.8-.8c1.6-1.6,1.6-4.3,0-6Z',
});

export function PersonDropdown(props?: { isNewLayout?: boolean }) {
  const auth = useStore(authStore);
  const ref = useRef(null);
  const { hasPermission } = usePermissions();
  const userRole = useUserRoleForCurrentWorkspace();
  const currentWorkspace = useCurrentWorkspace();
  const { isSampleWorkspace } = useSampleWorkspace();
  const workspaceSettingsLink = getWorkspaceSettingsLink();
  const { isLoading: isSetupGuideLoading, setupGuide } = useSetupGuideExperiment();
  const is2026LayoutRefresh = useFeatureFlag(FeatureFlag.LayoutRefresh2026);

  const showAdminMasqueradeOption =
    ((auth.account?.admin ?? false) && (auth.user?.can_masquerade ?? false)) || (auth.account?.is_masq ?? false);

  const isSetupGuideIncomplete = !isSetupGuideLoading && setupGuide.completed_at == null;

  const showSetUpWorkspaceNavItem =
    isSetupGuideIncomplete &&
    window.sessionStorage.getItem('showSetUpWorkspaceNavItem') === 'true' &&
    !isSampleWorkspace(currentWorkspace?.id);

  const [isShowingMasqBar, setIsShowingMasqBar] = useState(getMasqBarVisibility);
  const isShowingMasqBarRef = useRef(isShowingMasqBar);
  isShowingMasqBarRef.current = isShowingMasqBar;

  const onMasqueradeChange = useCallback(
    (event: React.FormEvent<HTMLInputElement>) => {
      event.preventDefault();

      const newValue = !isShowingMasqBarRef.current;
      setMasqBarVisibility(newValue);
      setIsShowingMasqBar(newValue);

      // If turning off masq bar, remove masq_account_id from URL
      if (!newValue) {
        const url = new URL(window.location.href);
        url.searchParams.delete('masq_account_id');
        window.location.href = url.toString();
      } else {
        // Reload the page, so that the Journeys UI picks up the local storage update
        window.location.reload();
      }
    },
    [setIsShowingMasqBar, setMasqBarVisibility],
  );

  const onHeartClick = () => {
    fireConfetti(ref.current, {
      // These hex values match the colors from the heart svg, with the addition of our brand colors
      colors: [
        BRAND_EVERGREEN,
        BRAND_VERDANT,
        '#ea3041',
        '#f77e44',
        '#fbd733',
        '#5fbb5a',
        '#2799dd',
        '#1566bf',
        '#6d28ae',
      ],
      shapes: [heartShape],
    });
  };

  const themeContext = useThemeContext();

  const isDarkModeToggleEnabled = useFeatureFlag(FeatureFlag.DarkModeToggle);

  const onDarkModeChange = () => {
    themeContext.toggleDarkMode();
  };

  return (
    <DropdownMenu>
      {(context) => (
        <IconButton
          label="Menu"
          icon="manage"
          size="md"
          ref={context.reference.setReference}
          isNewLayout={props?.isNewLayout}
          {...context.reference.getReferenceProps()}
        />
      )}
      <DropdownMenuContent>
        {userRole != null || showSetUpWorkspaceNavItem ? (
          <DropdownMenuGroup>
            {userRole != null ? (
              <DropdownMenuItem>
                {currentUserRoleString(userRole.name)}
                {userRole.redacted ? (
                  <Box style={{ width: '20px', height: '20px' }} ml="100">
                    <Icon name="view-off" size="fill" display="block" />
                  </Box>
                ) : null}
              </DropdownMenuItem>
            ) : null}
            {showSetUpWorkspaceNavItem ? (
              <DropdownMenuItem
                href={generatePath('/workspaces/:workspaceId/journeys/home/workspace-setup', {
                  workspaceId: currentWorkspace?.id ?? 0,
                })}
              >
                Set up workspace
              </DropdownMenuItem>
            ) : null}
          </DropdownMenuGroup>
        ) : null}

        <DropdownMenuGroup>
          <DropdownMenuItem href="/settings/personal">Personal settings</DropdownMenuItem>
          <DropdownMenuItem href="/settings/experimental-features">Experimental features</DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuGroup>
          <DropdownMenuItem href="/settings">Account settings</DropdownMenuItem>
          {hasPermission('account/team_members/edit') === true ? (
            <DropdownMenuItem href="/settings/team/add">Invite teammate</DropdownMenuItem>
          ) : null}
          <DropdownMenuItem href="/settings/privacy">Privacy, Data &amp; AI</DropdownMenuItem>
          {hasPermission('account/billing/view') === true ? (
            <DropdownMenuItem href="/settings/billing/your-plan">Plans &amp; Billing</DropdownMenuItem>
          ) : null}
          <DropdownMenuItem href={workspaceSettingsLink(currentWorkspace?.id ?? 'last')}>
            Workspace settings
          </DropdownMenuItem>
        </DropdownMenuGroup>
        {isDarkModeToggleEnabled ? (
          <DropdownMenuGroup>
            <DropdownMenuItem>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Text
                  mr="400"
                  display="inline"
                  as="label"
                  id="toggle-dark-mode-label"
                  htmlFor="toggle-dark-mode"
                  textTransform="capitalize"
                >
                  Dark mode
                </Text>
                <Toggle
                  size="sm"
                  labelOn="On"
                  labelOff="Off"
                  onChange={() => onDarkModeChange()}
                  ariaLabelledby="toggle-dark-mode-label"
                  id="toggle-dark-mode"
                  isChecked={themeContext.isDarkMode}
                />
              </Box>
            </DropdownMenuItem>
          </DropdownMenuGroup>
        ) : null}
        {showAdminMasqueradeOption ? (
          <DropdownMenuGroup>
            <DropdownMenuItem>
              <Box display="flex" alignItems="center">
                <Text
                  mr="400"
                  display="inline"
                  as="label"
                  id="toggle-admin-masq-label"
                  htmlFor="toggle-admin-masq"
                  textTransform="capitalize"
                >
                  Admin masquerade
                </Text>
                <Toggle
                  size="sm"
                  labelOn="On"
                  labelOff="Off"
                  onChange={(event) => onMasqueradeChange(event)}
                  ariaLabelledby="toggle-admin-masq-label"
                  id="toggle-admin-masq"
                  isChecked={isShowingMasqBar}
                />
              </Box>
            </DropdownMenuItem>
          </DropdownMenuGroup>
        ) : null}
        <DropdownMenuGroup>
          <DropdownMenuItem href="/logout" isDanger={true}>
            Log out
          </DropdownMenuItem>
        </DropdownMenuGroup>
        {is2026LayoutRefresh ? (
          <DropdownMenuFooter className={styles.dropdownFooter}>
            <Paragraph sizeVariant="xs" display="flex" flexWrap="wrap" alignItems="center" gap="050">
              <span id="made-with">Made with</span>
              <PlainButton
                aria-labelledby="made-with heart-love around-the-world"
                ref={ref}
                className={styles.heartContainer}
                onClick={onHeartClick}
              >
                <Icon className={styles.heartContainerHeartPlain} name="heart" id="heart-love" aria-label="love" />
                <Image className={styles.heartContainerHeartPride} src={heartPride} aria-label="love" />
              </PlainButton>
              <span id="around-the-world">around the world.</span>
            </Paragraph>
          </DropdownMenuFooter>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
